import * as Scene1 from './scenes/Scene1.js'
import * as Scene2 from './scenes/Scene2.js'
import * as Scene4 from './scenes/Scene4.js'
import { loadScene } from './SceneManager.js'
import { initializeSetup, applyTheme } from './setup.js'
import { CONFIG } from '../config.js'

// Initialize UI Setup (Hamburger menu, etc.)
initializeSetup();

// Map dropdown values to Scene Modules
const SCENE_MODULES = {
    "Scene 1": Scene1,
    "Scene 2": Scene2,
    "Scene 3": null, // Force generic logic
    "Scene 4": Scene4
};

/**
 * Main Application Entry
 */

const menu = document.getElementById('home-menu');
const uiContainer = document.querySelector('.ui-container');

// --- Menu Functions ---

window.enterScene = (sceneKey, updateUrl = true) => {
    // Check if the scene is defined in our map (even if null)
    if (Object.prototype.hasOwnProperty.call(SCENE_MODULES, sceneKey)) {
        const sceneModule = SCENE_MODULES[sceneKey]; // Might be null


        // Show Loading Screen
        const loadingScreen = document.getElementById('loading-screen');
        if (loadingScreen) loadingScreen.classList.remove('hidden');

        // 1. Prepare Config (Merge Global + Scene Specific)
        // If sceneModule has config, use it. Otherwise start with global CONFIG.
        let finalConfig = (sceneModule && sceneModule.config) ? sceneModule.config : { ...CONFIG };

        if (finalConfig.scenes && finalConfig.scenes[sceneKey]) {

            finalConfig = {
                ...finalConfig,
                ...finalConfig.scenes[sceneKey] // Merge top-level overrides (like model)
            };
        }

        // 2. Load the Scene
        loadScene(finalConfig, sceneModule, () => {
            // ON LOADED


            // Hide Loading Screen
            setTimeout(() => {
                if (loadingScreen) loadingScreen.classList.add('hidden');
            }, 500); // Small buffer for smoothness

            // Hide Menu / Show UI
            if (menu) menu.classList.add('hidden');
            if (uiContainer) uiContainer.style.display = 'block';
        });

        applyTheme(finalConfig);

        // 2.5 Add Body Class for Scene Specific Styling (e.g. .scene-scene-1)
        // Remove old scene classes
        document.body.classList.forEach(className => {
            if (className.startsWith('scene-')) document.body.classList.remove(className);
        });
        // Add new class
        const safeKey = sceneKey.replace(/\s+/g, '-').toLowerCase();
        document.body.classList.add(`scene-${safeKey}`);

        // Update Document Title
        if (finalConfig.title) {
            document.title = finalConfig.title;
        }

        // 3. Update UI Elements (Logo, etc.)
        const logoImg = document.querySelector('.logo');
        if (logoImg) {
            if (finalConfig.logo) {
                logoImg.src = finalConfig.logo;
            } else if (finalConfig.theme && finalConfig.theme.navBar && finalConfig.theme.navBar.logoPath) {
                logoImg.src = finalConfig.theme.navBar.logoPath;
            }
        }

        // 5. Apply Scene-Specific UI Positioning
        const instructionPanel = document.getElementById('instruction-panel');
        if (instructionPanel && finalConfig.ui && finalConfig.ui.instructionPanel) {
            const panelConfig = finalConfig.ui.instructionPanel;
            instructionPanel.style.top = panelConfig.top || 'auto';
            instructionPanel.style.right = panelConfig.right || 'auto';
            instructionPanel.style.bottom = panelConfig.bottom || 'auto';
            instructionPanel.style.left = panelConfig.left || 'auto';
        } else if (instructionPanel) {
            // Reset to default (bottom-left) for scenes without custom positioning
            instructionPanel.style.top = 'auto';
            instructionPanel.style.right = 'auto';
            instructionPanel.style.bottom = '20px';
            instructionPanel.style.left = '20px';
        }

        // 5.5. Apply Scene-Specific Top Instruction Text
        const topLabel = document.getElementById('top-instruction-label');
        if (topLabel && finalConfig.ui && finalConfig.ui.topInstructionText) {
            const textConfig = finalConfig.ui.topInstructionText;
            // Handle both string and object formats
            if (typeof textConfig === 'string') {
                topLabel.textContent = textConfig;
            } else if (textConfig.initial) {
                topLabel.textContent = textConfig.initial;
            }
            topLabel.style.display = 'block';
        } else if (topLabel) {
            // Reset to default text for scenes without custom text
            topLabel.textContent = 'Discover each stage of the operation by selecting the blue hotspots';
            
            if (finalConfig.ui && finalConfig.ui.hideTopInstruction) {
                topLabel.style.display = 'none';
            } else {
                topLabel.style.display = 'block';
            }
        }

        // 5.6. Hide/Show Explore Instruction based on scene config
        const exploreInstruction = document.getElementById('explore-instruction');
        if (exploreInstruction) {
            if (finalConfig.ui && finalConfig.ui.hideExploreInstruction) {
                exploreInstruction.style.display = 'none';
            } else {
                exploreInstruction.style.display = 'list-item';
            }
        }

        // 6. Reset Info Panel
        document.getElementById('info-panel').classList.remove('active');

        // 7. Update URL
        if (updateUrl) {
            const newUrl = new URL(window.location);
            newUrl.searchParams.set('experience', sceneKey);
            window.history.pushState({}, '', newUrl);
        }
    } else {
        console.error(`Scene '${sceneKey}' not found.`);
    }
};

window.returnToHome = () => {

    document.title = "Interactive Experiences";

    // 1. Show Menu / Hide UI (optional, maybe keep UI hidden)
    if (menu) menu.classList.remove('hidden');
    // We can hide the specific scene UI if needed, but for now just overlaying the menu is enough.

    // 2. Update URL
    const newUrl = new URL(window.location);
    newUrl.searchParams.delete('experience');
    window.history.pushState({}, '', newUrl);
};

// --- Initial Load Logic ---

const urlParams = new URLSearchParams(window.location.search);
const initialScene = urlParams.get('experience');

if (initialScene && Object.prototype.hasOwnProperty.call(SCENE_MODULES, initialScene)) {
    // If URL has a valid scene, load it and hide menu
    window.enterScene(initialScene, false);
} else {
    // Otherwise, show menu and load a "Background" scene (using Scene1 as default for visuals)

    document.title = "Interactive Experiences";
    loadScene(Scene1.config, Scene1); // Load it so there's something beautiful behind the glass
    if (menu) menu.classList.remove('hidden');
}

// Reset View Button
const resetBtn = document.getElementById('back-button');
if (resetBtn) {
    resetBtn.addEventListener('click', () => {
        if (window.SceneManager) window.SceneManager.resetView();
    });
}

// Handle Browser Back/Forward
window.addEventListener('popstate', (event) => {
    const params = new URLSearchParams(window.location.search);
    const sceneKey = params.get('experience');

    if (sceneKey && Object.prototype.hasOwnProperty.call(SCENE_MODULES, sceneKey)) {
        window.enterScene(sceneKey, false); // Don't push state again
    } else {
        window.returnToHome(); // Back to home if no param
    }
});

// SceneManager exposure moved to SceneManager.js for better access to internals

// SceneManager exposure moved to SceneManager.js for better access to internals